import type { BlockDef, LoadedApp } from '@/features/page-editor/store/types'
import type { BlockState, VariableDef } from '@cogo/types-v2'
import { queueSave } from './auto-save'
import { markProjectContentDirty } from './project-content-dirty'
import {
  readEnvelope,
  synthesizeUi,
  type AboxEnvelope,
} from '@/features/design-system/ontology/abox-render-prep'

// v3 BlockState has three sibling slots (state/computed/context) instead of a
// scope-keyed map. Map the legacy `scope` param (used by the Data Explorer UI)
// onto the appropriate slot; unknown scopes fall back to `state`.
export type BlockStateSlot = 'state' | 'computed' | 'context'
export function slotFor(scope: string): BlockStateSlot {
  if (scope === 'computed' || scope === 'context') return scope
  return 'state'
}
export interface DataSlice {
  // Variable management
  addVariable: (blockName: string, scope: string, name: string, def: { type: string; default?: unknown }) => void
  updateVariable: (blockName: string, scope: string, name: string, updates: Record<string, unknown>) => void
  removeVariable: (blockName: string, scope: string, name: string) => void

  // Datasource management
  addDatasource: (blockName: string, name: string, source: Record<string, unknown>) => void
  updateDatasource: (blockName: string, name: string, updates: Record<string, unknown>) => void
  removeDatasource: (blockName: string, name: string) => void

  // Event binding management
  addEventBinding: (blockName: string, eventKey: string, binding: { flow?: string; action?: string; params?: Record<string, unknown> }) => void
  removeEventBinding: (blockName: string, eventKey: string) => void

  // Datasource mapping (drag-to-connect)
  createDatasourceMapping: (dsBlockName: string, dsName: string, targetBlockName: string, targetKey: string) => void

  // Phase 051 D-051-04 — post-commit baseline reconciliation. Called by
  // save-controller AFTER a successful add_block. Locates the block via
  // inverse IRI → blockName linear scan (mirrors draft-store.ts:190-200
  // readBaseline shape; same precedent as data-slice.ts:53-66
  // updateVariable). Does NOT set s.isDirty (this is reconciliation, not
  // a user edit — Pattern 4 shape-divergence #2).
  commitBlockBaseline: (blockId: string, properties: Record<string, unknown>) => void
}

type StateWithProject = DataSlice & {
  currentProject: LoadedApp | null
  isDirty: boolean
  hasUncommittedArtifactLineage: boolean
}
type Set = (fn: (state: StateWithProject) => void) => void
type Get = () => StateWithProject

function queueDataBlockSave(
  state: StateWithProject,
  blockName: string,
  field: 'variables' | 'datasource' | 'events',
  data: unknown,
) {
  const studioState = state as StateWithProject & {
    projectSource?: string
    currentProjectId?: string | null
    blockIdMap?: Record<string, string>
  }
  if (studioState.projectSource !== 'server' || !studioState.currentProjectId) return

  const blockId = studioState.blockIdMap?.[blockName]
  if (!blockId) return

  queueSave({
    type: 'block',
    blockId,
    field,
    data,
  })
}

export function createDataSlice(
  set: Set,
  get: Get,
  _api: unknown,
): DataSlice {
  return {
    addVariable: (blockName, scope, name, def) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block) return
        if (!block.state) block.state = {} as BlockState
        const slot = slotFor(scope)
        const slotMap = (block.state[slot] ?? {}) as Record<string, unknown>
        slotMap[name] = def
        ;(block.state as Record<string, unknown>)[slot] = slotMap
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.state) queueDataBlockSave(get(), blockName, 'variables', block.state)
    },

    updateVariable: (blockName, scope, name, updates) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block) return
        const slot = slotFor(scope)
        const slotMap = block.state?.[slot] as Record<string, unknown> | undefined
        if (!slotMap?.[name]) return
        const existing = slotMap[name]
        if (typeof existing === 'object' && existing !== null) {
          Object.assign(existing, updates)
        } else {
          slotMap[name] = updates
        }
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.state) queueDataBlockSave(get(), blockName, 'variables', block.state)
    },

    removeVariable: (blockName, scope, name) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block) return
        const slot = slotFor(scope)
        const slotMap = block.state?.[slot] as Record<string, unknown> | undefined
        if (!slotMap) return
        delete slotMap[name]
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.state) queueDataBlockSave(get(), blockName, 'variables', block.state)
    },

    addDatasource: (blockName, name, source) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block) return
        if (!block.data) block.data = {}
        block.data[name] = source as never
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.data) queueDataBlockSave(get(), blockName, 'datasource', block.data)
    },

    updateDatasource: (blockName, name, updates) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block) return
        const src = block.data?.[name]
        if (!src) return
        Object.assign(src, updates)
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.data) queueDataBlockSave(get(), blockName, 'datasource', block.data)
    },

    removeDatasource: (blockName, name) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block) return
        if (!block.data) return
        delete block.data[name]
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.data) queueDataBlockSave(get(), blockName, 'datasource', block.data)
    },

    addEventBinding: (blockName, eventKey, binding) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block) return
        if (!block.events) block.events = {}
        if (!block.events.bindings) block.events.bindings = {}
        block.events.bindings[eventKey] = binding as never
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.events) queueDataBlockSave(get(), blockName, 'events', block.events)
    },

    removeEventBinding: (blockName, eventKey) => {
      set((s) => {
        const block = s.currentProject?.blocks[blockName]
        if (!block?.events?.bindings) return
        delete block.events.bindings[eventKey]
        markProjectContentDirty(s)
      })
      const block = get().currentProject?.blocks[blockName]
      if (block?.events) queueDataBlockSave(get(), blockName, 'events', block.events)
    },

    createDatasourceMapping: (dsBlockName, dsName, targetBlockName, targetKey) => {
      set((s) => {
        const targetBlock = s.currentProject?.blocks[targetBlockName]
        if (!targetBlock) return
        // Ensure target variable exists in the `state` slot of BlockState
        if (!targetBlock.state) targetBlock.state = {} as BlockState
        if (!targetBlock.state.state) targetBlock.state.state = {}
        if (!targetBlock.state.state[targetKey]) {
          targetBlock.state.state[targetKey] = { type: 'any', default: null } as VariableDef
        }
        // Set resultTarget on datasource (v3: flat DataMap at block.data)
        const dsBlock = s.currentProject?.blocks[dsBlockName]
        if (!dsBlock) return
        const source = dsBlock.data?.[dsName]
        if (source) {
          ;(source as unknown as Record<string, unknown>).resultTarget = targetKey
        }
        markProjectContentDirty(s)
      })
      const state = get()
      const targetBlock = state.currentProject?.blocks[targetBlockName]
      const dsBlock = state.currentProject?.blocks[dsBlockName]
      if (targetBlock?.state) queueDataBlockSave(state, targetBlockName, 'variables', targetBlock.state)
      if (dsBlock?.data) queueDataBlockSave(state, dsBlockName, 'datasource', dsBlock.data)
    },

    commitBlockBaseline: (blockId, properties) => set((s) => {
      if (!s.currentProject?.blocks) return
      // OQ#3 — linear scan; N ≤ 30 in practice. Mirrors
      // draft-store.ts:190-200 readBaseline shape (inverse IRI → blockName).
      for (const [name, block] of Object.entries(s.currentProject.blocks)) {
        const env = readEnvelope(block)
        if (env?.id !== blockId) continue
        const data = block.data as { __aboxEnvelope?: AboxEnvelope } | undefined
        if (!data?.__aboxEnvelope) return
        // D-051-04 — REPLACE the properties bag with the committed merged set
        // (the projected env.properties the controller just sent to add_block).
        // NOT a merge — the projected envelope is already the full merged
        // shape, so this is the post-commit baseline reconciliation.
        const nextEnvelope: AboxEnvelope = {
          ...data.__aboxEnvelope,
          properties: { ...properties },
        }
        // Phase 051.1 D-051.1-01 — Codex P1 fix. Re-synthesize the block's
        // `ui` from the committed envelope so `useProjectedApp`'s
        // drafts-empty fast-path returns the correct rendering immediately
        // after Save (no reload, no second draft cycle). Mirrors the
        // canonical `apply-drafts.ts:178-185` invocation verbatim:
        // synthesizeUi(blockType, blockName, properties, tapControlIds?, map?, blockId?).
        // For baseline reconciliation, NO new tap-control derivation is
        // needed (the block's owned-flow set is constant across baseline
        // and projected renders — RESEARCH OQ#4) and NO new dirty
        // aboxElementMap entries are accumulated; we pass `undefined` and
        // an empty `{}` respectively. The canonical `synthesizeUi` is
        // imported from `abox-render-prep` per D-051.1-02 — NO local
        // re-implementation, NO divergence (AP1.1).
        const ui = synthesizeUi(
          nextEnvelope.block_type,
          nextEnvelope.block_name || name,
          nextEnvelope.properties,
          undefined,
          {},
          nextEnvelope.id,
        )
        // INTENTIONAL: do NOT set s.isDirty = true here (Pattern 4
        // shape-divergence #2 — post-commit reconciliation, not user edit).
        // Cast via `unknown` — `BlockDef['data']` is `DataMap` (=
        // `Record<string, DataSource>`) but the `__aboxEnvelope` stash is
        // NOT a `DataSource`. This is the same documented type-bridge
        // pattern abox-render-prep.ts uses for the envelope read at L99
        // and that the abox loader uses on the stash-side.
        ;(s.currentProject.blocks as Record<string, BlockDef>)[name] = {
          ...block,
          ui,
          data: { ...(block.data as object), __aboxEnvelope: nextEnvelope } as unknown as BlockDef['data'],
        }
        return
      }
    }),
  }
}
